import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { motion } from 'framer-motion';
import { Play, Pause, SkipBack, SkipForward, Clock } from 'lucide-react';
import { Button } from '../ui/button';

const TimelineReplay = ({ filters, reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [timelineData, setTimelineData] = useState({ buckets: [], meta: {} });
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [granularity, setGranularity] = useState('hour');
  const intervalRef = useRef(null);
  const animationDuration = reducedMotion ? 0 : 0.3;

  useEffect(() => {
    fetchTimelineData();
  }, [filters, granularity]);

  useEffect(() => {
    if (isPlaying && timelineData.buckets.length > 0) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          if (prev >= timelineData.buckets.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 1000 / speed);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, speed, timelineData.buckets.length]);

  const fetchTimelineData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/analytics/timeline-replay?granularity=${granularity}`);
      const result = await response.json();
      setTimelineData(result);
      setCurrentIndex(0);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    setCurrentIndex(0);
    setIsPlaying(false);
  };

  const handleStepForward = () => {
    if (currentIndex < timelineData.buckets.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleStepBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return granularity === 'minute' 
      ? date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit' });
  };

  const currentBucket = timelineData.buckets[currentIndex];
  const maxCount = Math.max(...(timelineData.buckets?.map(b => b.count) || [1]));

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <Clock className="w-5 h-5 text-amber-400" />
          Timeline Replay
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Interactive chronological view of call activity • Play to watch events unfold
        </p>
      </CardHeader>
      <CardContent>
        {/* Granularity Toggle */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'hour', label: 'Hourly' },
            { key: 'minute', label: 'Per Minute' }
          ].map((gran) => (
            <button
              key={gran.key}
              onClick={() => setGranularity(gran.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                granularity === gran.key
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              {gran.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="animate-pulse text-slate-400">Loading timeline...</div>
          </div>
        ) : timelineData.buckets.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p>No timeline data available yet</p>
            <p className="text-xs mt-2">Timeline will populate as calls are made</p>
          </div>
        ) : (
          <div>
            {/* Timeline Visualization */}
            <div className="mb-6">
              <div className="relative h-32 bg-slate-800/50 rounded-lg overflow-hidden">
                {timelineData.buckets.map((bucket, index) => {
                  const height = (bucket.count / maxCount) * 100;
                  const isActive = index === currentIndex;
                  const isPast = index < currentIndex;
                  
                  return (
                    <motion.div
                      key={index}
                      className="absolute bottom-0 cursor-pointer"
                      style={{
                        left: `${(index / timelineData.buckets.length) * 100}%`,
                        width: `${100 / timelineData.buckets.length}%`,
                        height: `${height}%`,
                        backgroundColor: isActive 
                          ? '#f59e0b' 
                          : isPast 
                            ? '#C9A24A' 
                            : '#334155'
                      }}
                      initial={reducedMotion ? {} : { height: 0 }}
                      animate={{ height: `${height}%` }}
                      transition={{ duration: 0.3, delay: index * 0.02 }}
                      onClick={() => setCurrentIndex(index)}
                      whileHover={{ opacity: 0.8 }}
                    />
                  );
                })}
              </div>
            </div>

            {/* Current Bucket Details */}
            {currentBucket && (
              <motion.div
                key={currentIndex}
                initial={reducedMotion ? {} : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: animationDuration }}
                className="bg-slate-800/50 rounded-lg p-6 mb-6"
              >
                <div className="text-center mb-4">
                  <p className="text-2xl font-bold text-amber-400">
                    {formatTimestamp(currentBucket.timestamp)}
                  </p>
                  <p className="text-sm text-slate-400 mt-1">
                    {currentBucket.count} calls in this period
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  {/* Intents */}
                  <div>
                    <p className="text-xs text-slate-400 mb-2">Intents</p>
                    {Object.entries(currentBucket.intents || {}).map(([intent, count]) => (
                      <div key={intent} className="flex justify-between text-xs text-slate-300 mb-1">
                        <span>{intent.replace('_', ' ')}</span>
                        <span className="font-bold">{count}</span>
                      </div>
                    ))}
                  </div>

                  {/* Dispositions */}
                  <div>
                    <p className="text-xs text-slate-400 mb-2">Dispositions</p>
                    {Object.entries(currentBucket.dispositions || {}).map(([disp, count]) => (
                      <div key={disp} className="flex justify-between text-xs text-slate-300 mb-1">
                        <span>{disp.replace('_', ' ')}</span>
                        <span className="font-bold">{count}</span>
                      </div>
                    ))}
                  </div>

                  {/* Statuses */}
                  <div>
                    <p className="text-xs text-slate-400 mb-2">Statuses</p>
                    {Object.entries(currentBucket.statuses || {}).map(([status, count]) => (
                      <div key={status} className="flex justify-between text-xs text-slate-300 mb-1">
                        <span>{status.replace('_', ' ')}</span>
                        <span className="font-bold">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Playback Controls */}
            <div className="flex items-center justify-center gap-4">
              <Button size="sm" variant="outline" onClick={handleReset}>
                <SkipBack className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={handleStepBack} disabled={currentIndex === 0}>
                ← Step
              </Button>
              <Button 
                size="lg" 
                onClick={handlePlayPause}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </Button>
              <Button size="sm" variant="outline" onClick={handleStepForward} disabled={currentIndex === timelineData.buckets.length - 1}>
                Step →
              </Button>
              <div className="flex gap-2">
                {[1, 2, 5].map(s => (
                  <button
                    key={s}
                    onClick={() => setSpeed(s)}
                    className={`px-3 py-1 rounded text-xs ${
                      speed === s ? 'bg-amber-500 text-white' : 'bg-slate-700 text-slate-400'
                    }`}
                  >
                    {s}x
                  </button>
                ))}
              </div>
            </div>

            {/* Progress */}
            <div className="mt-4 text-center text-xs text-slate-400">
              {currentIndex + 1} / {timelineData.buckets.length} buckets
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TimelineReplay;
